import { orgSelectors } from 'ducks/orgs'
import { HardwareSupplierFilterKeyType, SellerIntegrationType } from 'opensolar-common'
import { useCallback, useMemo } from 'react'
import { useSelector, useStore } from 'react-redux'
import { RootState } from 'types/state'
import { getPreferenceSupplierConfigFromState } from './supplierPreferenceConfig'
import { manualCostIntegration } from './useEnabledHardwareSuppliers'

export const useGetSupplierConfig = () => {
  // Adding manual cost integration to the list of supplier configs to get the preference order for manual cost
  const supplierConfigs = [...useSelector(orgSelectors.getEnabledSellerIntegrationsConfig), manualCostIntegration]
  const store = useStore<RootState>()

  const _supplierConfigMap = useMemo(() => {
    return new Map(supplierConfigs.map((integration) => [integration.filterKey, integration]))
  }, [supplierConfigs])

  const getIsConnected = useCallback(
    (supplier: string) => {
      return _supplierConfigMap.get(supplier)?.isConnected ?? false
    },
    [_supplierConfigMap]
  )

  const getSupplierConfig = (hardwareSupplier: HardwareSupplierFilterKeyType): SellerIntegrationType | null => {
    if (!hardwareSupplier || hardwareSupplier === manualCostIntegration.filterKey) return null
    return _supplierConfigMap.get(hardwareSupplier) ?? null
  }

  const getPreferenceSupplierConfig = useCallback(
    (options?: { quoteReviewOnly?: boolean }) => {
      return getPreferenceSupplierConfigFromState(store.getState(), getIsConnected, options)
    },
    [getIsConnected, store]
  )

  return { getSupplierConfig, getPreferenceSupplierConfig, getIsConnected }
}

// Non-React version for use outside of React components
export const getSupplierConfigNonReact = (
  hardwareSupplier?: HardwareSupplierFilterKeyType
): SellerIntegrationType | null => {
  if (!hardwareSupplier) return null
  const state = window.reduxStore.getState()
  const enabledHardwareSuppliers = orgSelectors.getEnabledHardwareDistributorFilterKeys(state)
  const supplierConfigs = orgSelectors.getEnabledSellerIntegrationsConfig(state)

  if (!enabledHardwareSuppliers.includes(hardwareSupplier)) return null
  return supplierConfigs.find((config) => config.filterKey === hardwareSupplier) || null
}
